import React, {
  useCallback, useEffect, useMemo, useRef,
} from 'react';

import { useApolloClient } from '@apollo/react-hooks';
import useRouter from 'use-react-router/use-react-router';
import { useLoginHelper } from 'components/auth-ru/Hooks/useLoginHelper';
import VkIcon from '../assets/vk.svg';
import GoogleIcon from '../assets/google.svg';
import queries from '../schemas/queries.graphql';
import useTokenManager from './useTokenManager';
import { getSessionId } from '../tracking/middleware';

export default function useSocialAuth(funcs: { setResponseError?: any; isLogin: boolean } = { isLogin: true }) {
  const loginHelper = useLoginHelper(!funcs.isLogin);

  const popupRef = useRef<Window>();
  const popup = popupRef.current;

  const openPopup = useCallback(
    url => {
      if (!process.env.BROWSER) {
        return;
      }

      const left = (screen.width - 600) / 2;
      const top = (screen.height - 400) / 4;

      const height = window.innerHeight / 1.5;
      const width = 500;

      const params = `toolbar=no, location=no, directories=no, status=no, menubar=no, scrollbars=no, resizable=no, copyhistory=no, width=${width}, height=${height}, top=${top}, left=${left}`;

      console.log('Opening an Oauth popup');
      if (popup) {
        console.log('Existing popup, closing it');
        popup.close();
      }

      getSessionId().then((sessionId) => {
        const newPopup = window.open(
          `${url}?sessionId=${sessionId}`,
          'popup',
          params,
        );
        popupRef.current = newPopup;
      });
    },
    [popup],
  );

  const openFb = () => {
    openPopup(`${process.env.BACKEND_URL}/auth/facebook`);
  };

  const openVk = () => {
    openPopup(`${process.env.BACKEND_URL}/auth/vkontakte`);
  };

  const openGoogle = () => {
    openPopup(`${process.env.BACKEND_URL}/auth/google`);
  };

  const openYandex = () => {
    openPopup(`${process.env.BACKEND_URL}/auth/yandex`);
  };

  const onMessage = async event => {
    const {
      isSuccess, jwtToken, errorCode, error,
    } = event.data;
    if (event.origin === process.env.BACKEND_URL) {
      funcs.setResponseError && funcs.setResponseError(null);
      if (popup) {
        console.log('Closing popup');
        popup.close();
      }
      if (isSuccess) {
        await loginHelper.setJwtToken(jwtToken);
        await loginHelper.redirect();
      } else {
        funcs.setResponseError
          && funcs.setResponseError(error ? error.message : errorCode);
      }
    }
  };

  useEffect(() => () => {
    if (popup) {
      popup.close();
    }
  }, [popup]);

  useEffect(() => {
    if (!process.env.BROWSER) return () => {};

    window.addEventListener('message', onMessage);
    return () => {
      window.removeEventListener('message', onMessage);
    };
  });

  const icons = useMemo(
    () => [
      <VkIcon key="vk" onClick={openVk} />,
      <GoogleIcon key="gg" onClick={openGoogle} />,
    ],
    [],
  );

  const rawIcons = [
    // [FbIcon, 'fb', 'Facebook', openFb],
    [VkIcon, 'vk', 'Вконтакте', openVk],
    [GoogleIcon, 'g', 'Google', openGoogle],
  ];

  return {
    icons,
    popup,
    setPopup: newPopup => {
      popupRef.current = newPopup;
    },
    openPopup,
    rawIcons,
    openFb,
    openGoogle,
    openVk,
    openYandex,
  };
}
